import { useMemo } from 'react';

import { useMemberId } from '@trello/authentication';

import { useMemberNodeIdFragment } from './MemberNodeIdFragment.generated';
import { usePlannerWorkspaceIdFragment } from './PlannerWorkspaceIdFragment.generated';

interface WorkspaceInfo {
  workspaceAri: string;
  workspaceId: string | null | undefined;
}

/**
 * Custom hook to retrieve the workspace ARI (Atlassian Resource Identifier) and ID for the current member.
 *
 * This hook performs a two-step data fetch:
 * 1. Gets the member's node ID using the current member ID
 * 2. Uses the node ID to fetch the planner workspace information
 *
 * The return value is memoized to prevent unnecessary re-renders.
 *
 * @returns {WorkspaceInfo} An object containing:
 *   - workspaceAri: The full workspace ARI (e.g. "ari:cloud:trello::workspace/123")
 *   - workspaceId: The extracted workspace ID (e.g. "123")
 *
 * @example
 * const { workspaceAri, workspaceId } = useWorkspaceAri();
 * // workspaceAri: "ari:cloud:trello::workspace/123"
 * // workspaceId: "123"
 */
export const useWorkspaceAri = (): WorkspaceInfo => {
  const memberId = useMemberId();

  const { data: member } = useMemberNodeIdFragment({
    from: { id: memberId },
    optimistic: true,
  });

  const { data } = usePlannerWorkspaceIdFragment({
    from: { id: member?.nodeId },
    optimistic: true,
  });

  return useMemo(() => {
    const workspaceAri = data?.planner?.workspace?.id || '';
    // Extract just the ID part from the ARI (e.g. "workspace1" from "ari:cloud:trello::workspace/workspace1")
    const workspaceId = workspaceAri.split('/').pop() || '';

    return {
      workspaceAri,
      workspaceId,
    };
  }, [data?.planner?.workspace?.id]);
};
